export type TranslateFn = (key: string, options?: Record<string, unknown>) => string;

/** Shape of {@link import('@eon/api-client').ApiError} without a package dependency. */
export interface ApiErrorLike extends Error {
  readonly status: number;
  readonly code: string;
  readonly details?: unknown;
}

const MESSAGE_KEYS: Record<string, string> = {
  'invalid credentials': 'errors.auth.invalidCredentials',
  'account disabled': 'errors.auth.accountDisabled',
  'email and password are required': 'errors.auth.emailPasswordRequired',
  'invalid session': 'errors.auth.invalidSession',
  'session expired': 'errors.auth.sessionExpired',
  'invalid code': 'errors.auth.invalidCode',
  'tester login disabled': 'errors.auth.testerLoginDisabled',
  'invalid tester code': 'errors.auth.invalidTesterCode',
  'missing refresh token': 'errors.auth.missingRefreshToken',
  'invalid refresh token': 'errors.auth.invalidRefreshToken',
  'refresh token expired': 'errors.auth.refreshTokenExpired',
  'old password incorrect': 'errors.auth.oldPasswordIncorrect',
  'invalid token': 'errors.auth.invalidToken',
  'token expired': 'errors.auth.tokenExpired',
  'phone is required': 'errors.auth.phoneRequired',
  'sessionid and code are required': 'errors.auth.sessionIdCodeRequired',
  'missing refresh cookie': 'errors.auth.missingRefreshCookie',
  'authentication required': 'errors.auth.authenticationRequired',
  'passwords do not match': 'errors.auth.passwordsDoNotMatch',
  'device not found': 'errors.entity.deviceNotFound',
  'user not found': 'errors.entity.userNotFound',
  'group not found': 'errors.entity.groupNotFound',
  'id tag not found': 'errors.entity.idTagNotFound',
  'station not found': 'errors.entity.stationNotFound',
  'session not found': 'errors.entity.sessionNotFound',
  'organization not found': 'errors.entity.organizationNotFound',
  'no ongoing charge': 'errors.entity.noOngoingCharge',
  'invalid deviceid': 'errors.validation.invalidDeviceId',
  'invalid userid': 'errors.validation.invalidUserId',
  'invalid groupid': 'errors.validation.invalidGroupId',
  'invalid idtagid': 'errors.validation.invalidIdTagId',
  'invalid stationid': 'errors.validation.invalidStationId',
  'invalid sessionid': 'errors.validation.invalidSessionId',
  'device belongs to another organization': 'station.link.orgConflict',
  'name required': 'errors.validation.nameRequired',
  'sessionid required': 'errors.validation.sessionIdRequired',
  'email or phonenumber required': 'errors.validation.emailOrPhoneRequired',
  'phonenumber required': 'errors.validation.phoneRequired',
  'email invitations are temporarily disabled; provide a phone number': 'errors.invite.emailDisabled',
  'email invitations are temporarily disabled': 'errors.invite.emailDisabled',
  'sms provider unavailable': 'errors.invite.smsUnavailable',
  'invitation not found': 'errors.invite.notFound',
  'invitation is not pending': 'errors.invite.notPending',
  'invitation has expired': 'errors.invite.expired',
  'no organization for user': 'errors.validation.noOrganizationForUser',
  'serial number required': 'device.create.serialRequired',
  'serial number already exists': 'errors.device.serialExists',
};

const REASON_KEYS: Record<string, string> = {
  invalid_credentials: 'errors.auth.invalidCredentials',
  account_disabled: 'errors.auth.accountDisabled',
};

const CODE_FALLBACK: Record<string, string> = {
  unauthorized: 'errors.unauthorized',
  forbidden: 'errors.forbidden',
  invalid_argument: 'errors.invalidRequest',
  not_found: 'errors.notFound',
  already_exists: 'errors.conflict',
  conflict: 'errors.conflict',
  rate_limited: 'errors.rateLimited',
  internal: 'errors.internal',
  unavailable: 'errors.unavailable',
  failed_precondition: 'errors.invalidRequest',
  unknown: 'errors.generic',
};

function normalizeMessage(message: string): string {
  return message.trim().toLowerCase();
}

export function isApiError(err: unknown): err is ApiErrorLike {
  return (
    err instanceof Error &&
    err.name === 'ApiError' &&
    typeof (err as ApiErrorLike).code === 'string' &&
    typeof (err as ApiErrorLike).status === 'number'
  );
}

function getReason(details: unknown): string | undefined {
  if (!details || typeof details !== 'object') return undefined;
  const reason = (details as { reason?: unknown }).reason;
  return typeof reason === 'string' ? reason : undefined;
}

function isNetworkError(err: Error): boolean {
  const msg = err.message.toLowerCase();
  return (
    msg === 'failed to fetch' ||
    msg === 'networkerror when attempting to fetch resource.' ||
    msg.includes('network request failed')
  );
}

/**
 * Maps API / client errors to a user-facing, localized string.
 * Never surfaces raw backend `message` text to the UI.
 */
export function resolveApiError(err: unknown, t: TranslateFn): string {
  if (isApiError(err)) {
    const reason = getReason(err.details);
    if (reason) {
      const reasonKey = REASON_KEYS[reason];
      if (reasonKey) return t(reasonKey);
    }

    const messageKey = MESSAGE_KEYS[normalizeMessage(err.message)];
    if (messageKey) return t(messageKey);

    const codeKey = CODE_FALLBACK[err.code];
    if (codeKey) return t(codeKey);

    return t('errors.generic');
  }

  if (err instanceof Error) {
    if (isNetworkError(err)) return t('errors.network');
    if (err.message) return err.message;
  }

  return t('errors.generic');
}
